<template>
    <div class="modal-card card">
        <div class="card-header">
            <div>
                <h4 class="card-header-title mb-0">{{ modalData.title || 'Settings Updated' }}</h4>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="card-body">
            <p class="conflict-subtitle mt-0 mb-2">{{ subtitle }}</p>
            <!-- Who changed it, so the user knows who to go ask -->
            <div class="conflict-meta" v-if="details.updated_by || details.updated_at">
                <i class="fa fa-user-pen mt-1"></i>
                <span>
                    Saved
                    <template v-if="details.updated_by"> by <strong>{{ details.updated_by }}</strong></template>
                    <template v-if="details.updated_at"> on {{ DateTimeFormat(details.updated_at) }}</template>
                </span>
            </div>

            <p v-if="!changedFields.length" class="conflict-identical mb-0">
                {{ identicalMessage }}
            </p>
            <div v-else>
                <div class="conflict-columns">
                    <div class="conflict-column-head yours">
                        <span class="conflict-column-title">{{ labels.yours }}</span>
                        <span class="conflict-column-note">{{ labels.yoursNote }}</span>
                    </div>
                    <div class="conflict-column-head theirs">
                        <span class="conflict-column-title">{{ labels.theirs }}</span>
                        <span class="conflict-column-note">{{ labels.theirsNote }}</span>
                    </div>
                </div>

                <div v-for="field in changedFields" :key="field.id" class="conflict-field">
                    <p class="conflict-field-name">{{ field.label }}</p>
                    <Diff mode="split" :theme="$store.state.darkMode ? 'dark' : 'light'" language="plaintext"
                        :prev="field.yours" :current="field.theirs" class="py-0" />
                </div>
            </div>
        </div>

        <div class="card-footer">
            <span class="text-danger conflict-error">{{ error }}</span>
            <div class="d-flex gap-2">
                <button class="btn btn-light" @click="onReviewSettings" :disabled="loading">
                    Review settings
                    <span class="fa fa-circle-info text-muted ms-0" style="font-size: 0.85rem; margin-left: 5px;"
                        title="Open the settings page to review the updated settings. Your message will not be sent."
                        data-bs-toggle="tooltip" data-bs-placement="top">
                    </span>
                </button>
                <button class="btn btn-primary" @click="onAcknowledge" :disabled="loading">
                    <span v-if="loadingAction === 'acknowledge'" class="spinner-border spinner-border-sm me-1"
                        role="status">
                        <span class="visually-hidden">Continuing...</span>
                    </span>
                    Accept and continue
                    <span class="fa fa-circle-info text-light ms-0" style="font-size: 0.85rem; margin-left: 5px;"
                        title="Continue to chat with the updated settings." data-bs-toggle="tooltip"
                        data-bs-placement="top">
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTimeFormat } from '../../utils/commonFunction';

/**
 * Raised by the PAICE chat when a send is rejected with 412 — the cid's settings
 * changed after the chat was opened, so the message would be answered under a
 * prompt/model the user never saw.
 *
 * Exclusive to the chat. The settings page has no counterpart: its writes are
 * unconditional server-side (last writer wins), so there is no conflict there to
 * resolve. Nothing of the user's is at stake here either — they are shown what
 * moved and confirm before the message goes out.
 */
export default {
    name: "PaiceSettingsConflict",
    props: {
        modalData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            error: "",
            loadingAction: "",
        };
    },
    computed: {
        details() {
            return this.modalData.details || {};
        },
        loading() {
            return !!this.loadingAction;
        },
        subtitle() {
            return this.modalData.subtitle
                || 'Settings were updated since you opened the chat. Your message will be sent using the new settings.';
        },
        identicalMessage() {
            return 'The prompt and model are unchanged. Continue to send your message.';
        },
        labels() {
            return {
                yours: 'When you opened the chat',
                yoursNote: 'No longer in effect',
                theirs: 'Now in effect',
                theirsNote: 'Will be used for your message',
            };
        },
        /**
         * Only show fields that actually differ. Both settings fields are always
         * carried through, but a diff of an unchanged field is noise that hides
         * the one the user actually needs to see.
         */
        changedFields() {
            return (this.modalData.fields || []).filter((f) => f.yours !== f.theirs);
        },
    },
    methods: {
        DateTimeFormat,
        async run(action, handler) {
            if (!handler) return;
            this.loadingAction = action;
            this.error = "";
            try {
                await handler();
            } catch (e) {
                this.error = e.response?.data?.detail || e.response?.data?.error || e.message;
                console.error("PAICE settings conflict action failed: ", e);
            } finally {
                this.loadingAction = "";
            }
        },
        onAcknowledge() {
            return this.run('acknowledge', this.modalData.onAcknowledge);
        },
        /**
         * Leave the chat for the settings page so the user can see the new
         * settings in full. The pending message is deliberately not sent and the
         * new settings are not adopted — returning to the chat and sending again
         * re-raises this notice.
         */
        onReviewSettings() {
            this.$store.dispatch('hideModal');
            this.$router.push({
                name: 'PAICE Settings',
                params: { cid: this.$route.params.cid || this.$store.state.currentClient?.cid },
            });
        },
    },
};
</script>

<style scoped>
.card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.conflict-subtitle {
    font-size: 0.75rem;
    color: #95aac9;
    margin-top: 0.15rem;
}

.conflict-meta {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: #fdf3e3;
    color: #916b30;
    border-radius: 0.375rem;
    padding: 0.55rem 0.75rem;
    font-size: 0.78rem;
    margin-bottom: 1.25rem;
}

.conflict-meta i {
    margin-top: 0.15rem;
    font-size: 0.75rem;
}

.conflict-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 0.5rem;
}

.conflict-column-head {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.5rem 0.65rem;
}

.conflict-column-head.yours {
    background: #f5f7f8;
    border-left-color: #95aac9;
}

.conflict-column-head.theirs {
    background: #e8f1fd;
    border-left-color: #2c7be5;
}

.conflict-column-title {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #12263f;
}

.conflict-column-note {
    font-size: 0.68rem;
    color: #95aac9;
}

.conflict-field {
    margin-top: 1rem;
}

.conflict-field-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: #2c7be5;
    margin-bottom: 0rem;
}

.conflict-identical {
    font-size: 0.8rem;
    color: #95aac9;
    text-align: center;
}

.card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.conflict-error {
    font-size: 0.78rem;
    overflow-wrap: break-word;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {

    .conflict-subtitle,
    .conflict-column-note,
    .conflict-identical {
        color: $dark-text-muted !important;
    }

    .conflict-meta {
        background: rgba(217, 130, 43, 0.14) !important;
        color: #e0a458 !important;
    }

    .conflict-column-head.yours {
        background: $dark-elevated-bg !important;
    }

    .conflict-column-head.theirs {
        background: $dark-selected-bg !important;
    }

    .conflict-column-title {
        color: $dark-text-primary !important;
    }

    .conflict-field-name {
        color: #7ab0ff !important;
    }
}
</style>
